import type { StatusColor } from '@/components/base/status-cell'
import { fromPrecision, type PrecisionAmount } from '@/utils/currency'

/** Natural Monday-to-Sunday order for weekday sorting across promotion UIs. */
export const DAY_ORDER = ['MONDAY', 'TUESDAY', 'WEDNESDAY', 'THURSDAY', 'FRIDAY', 'SATURDAY', 'SUNDAY'] as const

export type DayOfWeekName = (typeof DAY_ORDER)[number]

/** Sort weekday values (e.g. 'MONDAY') naturally rather than alphabetically. */
export const sortDaysOfWeek = <T extends string>(days: T[]): T[] =>
	[...days].sort((a, b) => DAY_ORDER.indexOf(a as DayOfWeekName) - DAY_ORDER.indexOf(b as DayOfWeekName))

export type PromotionStatusFields = {
	enabled: boolean
	startDate?: number | null
	endDate?: number | null
}

type PromotionStatusCopy = {
	Inactive: () => string
	Expired: () => string
	Scheduled: () => string
	Active: () => string
}

export const getPromotionScheduleStatus = (
	promotion: PromotionStatusFields,
	copy: PromotionStatusCopy
): { text: string; color: StatusColor } => {
	if (!promotion.enabled) {
		return { text: copy.Inactive(), color: 'red' }
	}

	const now = Date.now()
	if (promotion.endDate && promotion.endDate < now) {
		return { text: copy.Expired(), color: 'grey' }
	}
	if (promotion.startDate && promotion.startDate > now) {
		return { text: copy.Scheduled(), color: 'blue' }
	}
	return { text: copy.Active(), color: 'green' }
}

/**
 * Native stores a fraction (0.20 = 20%) so checkout can multiply price by it.
 * Legacy BC rows stored percent points (20 = 20%); treat those as already in percent.
 */
export const storedPercentageToPercentPoints = (amount: PrecisionAmount): number => {
	const n = fromPrecision(amount)
	return n > 1 ? n : n * 100
}
